/* eslint-disable typescript-enum/no-enum */

import type { default as VirtualBackgroundExtension } from 'agora-extension-virtual-background';
import type { IAgoraRTCRemoteUser, ILocalTrack, IRemoteTrack, UID } from 'agora-rtc-sdk-ng';
import type { SignalEvent } from 'dreamstate';

import type { Callable, Id, Maybe, Optional } from '@vimeo/core/types/generic';

import type { EStreamQuality, IAgoraDualConnectionConfig, TAgoraSDK } from '../types/agora';
import type { EEventConnectionType, IStreamHealthCredentials } from '../types/broadcaster';
import type {
  EComposerSessionType,
  IBroadcasterComposerSessionInfoMetadata,
  IGuestComposerSessionInfoMetadata,
  TComposerSessionId,
} from '../types/composer_session';
import type { EIngestStatus } from '../types/event';
import type {
  IFirebaseApiConfiguration,
  IFirebaseDependencies,
  TFirebaseApp,
} from '../types/firebase';
import type {
  EGraphicsDescriptorType,
  IGraphicsScene,
  IGraphicsSceneMetadata,
} from '../types/graphics';
import type { IGuestSeat } from '../types/guest';
import type { IInteractionApplicationUser } from '../types/interaction_room';
import type {
  IInteractionBaseFirebaseConfig,
  IInteractionToolsMetadata,
  IPoll,
  IQnAQuestion,
} from '../types/interaction_tools';
import type { EAudioMixingMode } from '../types/media';
import type { IConfirmationNotification, ISimpleNotification } from '../types/notifications';

/**
 * todo: it becomes bloated, unify all signals in this file and later create signals folder.
 */

export enum EPageSignal {
  PAGE_INACTIVE = 'PAGE_INACTIVE',
  PAGE_KEY_PRESSED = 'PAGE_KEY_PRESSED',
  PAGE_DRAG_END = 'PAGE_DRAG_END',
}

export enum ESceneCreateActionsSignal {
  CREATE_SCENE = 'CREATE_SCENE',
  INITIALIZE_SCENES = 'INITIALIZE_SCENES',
  INSERT_SCENES_ON_INDEX = 'INSERT_SCENES_ON_INDEX',
  MOVE_SCENE_TO_INDEX_OF_LIST = 'MOVE_SCENE_TO_INDEX_OF_LIST',
  DUPLICATE_SCENE = 'DUPLICATE_SCENE',
  SCROLL_TO_SCENE_ELEMENT = 'SCROLL_TO_SCENE_ELEMENT',
  SCROLL_TO_SCENE_ID = 'SCROLL_TO_SCENE_ID',
  SCROLL_TO_SCENE_LIST_END = 'SCROLL_TO_SCENE_LIST_END',
  INSERT_TEMPLATE_TO_INDEX_OF_LIST = 'INSERT_TEMPLATE_TO_INDEX_OF_LIST',
  INSERT_TEMPLATE_ITEM_TO_INDEX_OF_LIST = 'INSERT_TEMPLATE_ITEM_TO_INDEX_OF_LIST',
}

export type TMoveSceneToIndexSignalEvent = SignalEvent<{
  sceneId: Id;
  sceneListType: string;
  targetIndex: number;
}>;

export type TInsertTemplateToIndexSignalEvent = SignalEvent<{
  templateId: Id;
  sceneListType: string;
  targetIndex: number;
}>;

export type TInsertTemplateItemToIndexSignalEvent = SignalEvent<{
  templateId: Id;
  sceneListType: string;
  targetIndex: number;
}>;

export type TCreateSignalEvent = SignalEvent<{
  sceneListType: string;
  graphics?: {
    graphicType: EGraphicsDescriptorType;
    graphicId: string;
  };
  meta?: Partial<IGraphicsSceneMetadata>;
}>;

export type TInsertScenesSignalEvent = SignalEvent<{
  sceneListType: string;
  index: number;
}>;

export type TDuplicateSceneSignalEvent = SignalEvent<{
  scene: IGraphicsScene;
  sceneListType: string;
}>;

export type TScrollToSceneElementSignalEvent = SignalEvent<{
  element: HTMLElement;
}>;

export type TScrollToSceneIdSignalEvent = SignalEvent<{
  id: Maybe<Id>;
}>;

export type TScrollToSceneListEndSignalEvent = SignalEvent<{
  sceneListType: Maybe<string>;
}>;

export type TPageKeyPressedSignalEvent = SignalEvent<KeyboardEvent>;

export enum ELiveSignal {
  COMPOSER_SESSION_READY = 'COMPOSER_SESSION_READY',
  INTERACTION_SESSION_READY = 'INTERACTION_SESSION_READY',
  INTERACTION_SESSION_LOGOUT = 'INTERACTION_SESSION_LOGOUT',
  STREAM_HEALTH_CONFIG_READY = 'STREAM_HEALTH_CONFIG_READY',
  GLOBAL_ERROR = 'GLOBAL_ERROR',
  LIVE_EVENT_STARTING = 'LIVE_EVENT_STARTING',
  LIVE_EVENT_ENDED = 'LIVE_EVENT_ENDED',
  LIVE_EVENT_EXPIRED = 'LIVE_EVENT_EXPIRED',
  LIVE_EVENT_STATUS_UPDATED = 'LIVE_EVENT_STATUS_UPDATED',
  CONFIRMATION_NOTIFICATION = 'CONFIRMATION_NOTIFICATION',
  CONFIRMATION_NOTIFICATION_CLEAR = 'CONFIRMATION_NOTIFICATION_CLEAR',
  // @deprecated, use left and right instead.
  LIVE_PANEL_TAB_CHANGE_REQUEST = 'LIVE_PANEL_TAB_CHANGE_REQUEST',
  LEFT_PANEL_TAB_CHANGE_REQUEST = 'LEFT_PANEL_TAB_CHANGE_REQUEST',
  RIGHT_PANEL_TAB_CHANGE_REQUEST = 'RIGHT_PANEL_TAB_CHANGE_REQUEST',
  TRACK_SAFELY = 'TRACK_SAFELY',
  SHOW_SIMPLE_NOTIFICATION = 'SHOW_SIMPLE_NOTIFICATION',
  SIMULTANEOUS_STREAM_LIMIT_EXCEEDED = 'SIMULTANEOUS_STREAM_LIMIT_EXCEEDED',
  PROVISION_LIMIT_EXCEEDED = 'PROVISION_LIMIT_EXCEEDED',
  TIME_STREAM_LIMIT_EXCEEDED = 'TIME_STREAM_LIMIT_EXCEEDED',
  ADMIN_CONNECTION_TYPE_DETECTED = 'ADMIN_CONNECTION_TYPE_DETECTED',
  GUESTS_PANEL_COLLAPSE = 'GUESTS_PANEL_COLLAPSE',
  AUDIO_MIXING_MODE_CHANGED = 'AUDIO_MIXING_MODE_CHANGED',
  RTMP_STREAM_KEY_RECEIVED = 'RTMP_STREAM_KEY_RECEIVED',
  CLOUD_SESSION_ID_CHANGED = 'CLOUD_SESSION_ID_CHANGED',
  ACCORDION_ITEM_CHANGE_REQUEST = 'ACCORDION_ITEM_CHANGE_REQUEST',
  EVENT_TITLE_UPDATED = 'EVENT_TITLE_UPDATED',
  COMPOSER_SESSION_COMPLETED = 'COMPOSER_SESSION_COMPLETED',
  LIVE_ENCODER_TYPE_CHANGED = 'LIVE_ENCODER_TYPE_CHANGED',
  USER_TALKING_WHILE_MUTED = 'USER_TALKING_WHILE_MUTED',
  ADD_SPEAKER_TO_SCENE = 'ADD_SPEAKER_TO_SCENE',
}

export type TGlobalErrorSignalEvent = SignalEvent<Error>;

export type TSafelyTrackingSignalEvent = SignalEvent<Callable>;

export type TConfirmationNotificationSignalEvent = SignalEvent<IConfirmationNotification>;

export type TSimpleNotificationSignalEvent = SignalEvent<ISimpleNotification>;

export type TConfirmationNotificationClearSignalEvent = SignalEvent<number | undefined>;

export type TLivePanelChangeRequestSignalEvent = SignalEvent<number>;

export type TAccordionChangeRequestSignalEvent = SignalEvent<string>;

export type TLiveEventStatusUpdatedSignalEvent = SignalEvent<EIngestStatus>;

export type TComposerSessionReadySignalEvent = SignalEvent<{
  id: TComposerSessionId;
  type: EComposerSessionType;
  metadata: IBroadcasterComposerSessionInfoMetadata | IGuestComposerSessionInfoMetadata;
}>;

export type TInteractionSessionReadySignalEvent = SignalEvent<{
  roomId: Id;
  roomHash: string;
  user: IInteractionApplicationUser;
  firebase: IInteractionBaseFirebaseConfig;
  metadata: IInteractionToolsMetadata;
}>;

export type TStreamHealthConfigReadySignalEvent = SignalEvent<{
  firebase: IFirebaseDependencies;
  app: TFirebaseApp;
  credentials: IStreamHealthCredentials;
}>;

export type TRtmpSteamKeyReceivedSignalEvent = SignalEvent<string>;

export type TCloudSessionIdChangedSignalEvent = SignalEvent<{
  cloudSessionId: Optional<string>;
}>;

export enum ELiveRealtimeSignal {
  FIREBASE_INTERACTION_APP_READY = 'FIREBASE_INTERACTION_APP_READY',
  FIREBASE_COMPOSER_APP_READY = 'FIREBASE_COMPOSER_APP_READY',
}

export type TFirebaseInteractionAppReadySignalEvent = SignalEvent<{
  firebase: IFirebaseDependencies;
  app: TFirebaseApp;
  user: IInteractionApplicationUser;
  metadata: Maybe<IInteractionToolsMetadata>;
  roomId: Id;
  roomHash: string;
}>;

export type TFirebaseComposerAppReadySignalEvent = SignalEvent<{
  firebase: IFirebaseDependencies;
  app: TFirebaseApp;
  sessionId: Id;
  sessionFolder: string;
  config: IFirebaseApiConfiguration;
}>;

export type TAdminConnectionDetectedSignalEvent = SignalEvent<{
  switchToBroadcasterMode(): Promise<void>;
}>;

export enum EDependencySignal {
  FIREBASE_DEPENDENCIES_LOADED = 'FIREBASE_DEPENDENCIES_LOADED',
  AGORA_DEPENDENCIES_LOADED = 'AGORA_DEPENDENCIES_LOADED',
}

export type TFirebaseDependenciesLoadedSignalEvent = SignalEvent<IFirebaseDependencies>;

export type TAgoraDependenciesLoadedSignalEvent = SignalEvent<{
  agoraSDK: TAgoraSDK;
  VirtualBackgroundExtension: typeof VirtualBackgroundExtension;
}>;

export enum EAgoraSignal {
  AGORA_CONFIG_LOADED = 'AGORA_CONFIG_LOADED',
  AGORA_CONNECTION_REMOVED_FROM_SESSION = 'AGORA_CONNECTION_REMOVED_FROM_SESSION',
  AGORA_TOKEN_EXPIRED = 'AGORA_TOKEN_EXPIRED',
  AGORA_TOKEN_FAILED_TO_REFRESH = 'AGORA_TOKEN_FAILED_TO_REFRESH',
  AGORA_MEDIA_RECEIVED = 'AGORA_MEDIA_RECEIVED',
  AGORA_MEDIA_REMOVED = 'AGORA_MEDIA_REMOVED',
  AGORA_MEDIA_QUALITY_CHANGE_REQUIRED = 'AGORA_MEDIA_QUALITY_CHANGE_REQUIRED',
  AGORA_MEDIA_BULK_QUALITY_CHANGE_REQUIRED = 'AGORA_MEDIA_BULK_QUALITY_CHANGE_REQUIRED',
  AGORA_MEDIA_QUALITY_CHANGED = 'AGORA_MEDIA_QUALITY_CHANGED',
  AGORA_USER_LEFT = 'AGORA_USER_LEFT',
  ALLOWED_REMOTE_GUEST_ADDED = 'ALLOWED_REMOTE_GUEST_ADDED',
  ALLOWED_REMOTE_GUEST_REMOVED = 'ALLOWED_REMOTE_GUEST_REMOVED',
  LOCAL_MEDIA_DEVICE_OCCUPIED_BY_ANOTHER_APP = 'LOCAL_MEDIA_DEVICE_OCCUPIED_BY_ANOTHER_APP',
}

export type TAllowedRemoteGuestAddedSignalEvent = SignalEvent<IGuestSeat>;

export type TAllowedRemoteGuestRemovedSignalEvent = SignalEvent<IGuestSeat>;

export type TAgoraMediaReceivedSignalEvent = SignalEvent<{
  mediaType: 'audio' | 'video';
  user: IAgoraRTCRemoteUser;
  track: IRemoteTrack;
}>;

export type TAgoraMediaRemovedSignalEvent = SignalEvent<{
  mediaType: 'audio' | 'video';
  user: IAgoraRTCRemoteUser;
}>;

export type TAgoraUserLeftSignalEvent = SignalEvent<IAgoraRTCRemoteUser>;

export type TAgoraMediaQualityChangeRequired = SignalEvent<{ uid: UID; quality: EStreamQuality }>;

export type TAgoraBulkMediaQualityChangeRequired = SignalEvent<Record<UID, EStreamQuality>>;

export type TAgoraMediaQualityChanged = SignalEvent<{ uid: UID; quality: EStreamQuality }>;

export type TAgoraConnectionConfigLoaded = SignalEvent<IAgoraDualConnectionConfig>;

export type TAudioMixingModeChanged = SignalEvent<EAudioMixingMode>;

export type TGuestMuteChangeRequested = SignalEvent<{ guestId: string }>;

export enum ELiveMediaSignal {
  LOCAL_TRACK_UPDATED = 'LOCAL_TRACK_UPDATED',
  LOCAL_TRACK_DISPOSED = 'LOCAL_TRACK_DISPOSED',
  LOCAL_MEDIA_INITIALIZED = 'LOCAL_MEDIA_INITIALIZED',
  LOCAL_MEDIA_ACCESS_DENIED = 'LOCAL_MEDIA_ACCESS_DENIED',
  SCREEN_MEDIA_STARTED = 'SCREEN_MEDIA_STARTED',
  SCREEN_MEDIA_STOPPED = 'SCREEN_MEDIA_STOPPED',
  SCREEN_MEDIA_START_REQUIRED = 'SCREEN_MEDIA_START_REQUIRED',
  SCREEN_MEDIA_STOP_REQUIRED = 'SCREEN_MEDIA_STOP_REQUIRED',
  OUTPUT_DEVICE_CHANGED = 'OUTPUT_DEVICE_CHANGED',
}

export type TLocalTrackDisposedSignalEvent = SignalEvent<ILocalTrack>;

export enum ELiveBroadcasterSignal {
  SCENE_IN_PROGRAM_CHANGED = 'SCENE_IN_PROGRAM_CHANGED',
  EVENT_CONNECTION_TYPE_CHANGED = 'EVENT_CONNECTION_TYPE_CHANGED',
}

export type TEventConnectionTypeChangedSignalEvent = SignalEvent<EEventConnectionType>;

export enum ELiveGraphicsSignal {
  SCENE_PUSHED = 'SCENE_PUSHED',
  QNA_QUESTION_SELECTED = 'QNA_QUESTION_SELECTED',
  QNA_QUESTION_DESELECTED = 'QNA_QUESTION_DESELECTED',
  GUEST_UPDATED = 'GUEST_UPDATED',
  GUEST_DELETED = 'GUEST_DELETED',
  POLL_OBJECT_UPDATED = 'POLL_OBJECT_UPDATED',
  POLL_OBJECT_REMOVED = 'POLL_OBJECT_REMOVED',
  GRAPHIC_DELETED = 'GRAPHIC_DELETED',
  IMAGE_ADDED_BEHIND_GUEST = 'IMAGE_ADDED_BEHIND_GUEST',
  SCENE_VIDEO_STOPPED_PLAYING = 'SCENE_VIDEO_STOPPED_PLAYING',
  SHOW_VIDEO_PLAY_CONTROLS_DISABLED_NOTICE = 'SHOW_VIDEO_PLAY_CONTROLS_DISABLED_NOTICE',
  GRAPHIC_ADDED_TO_SCENE = 'GRAPHIC_ADDED_TO_SCENE',
  SCENE_IN_PROGRAM_CHANGED = 'SCENE_IN_PROGRAM_CHANGED',
  TOGGLE_MEDIA_IN_SCENE = 'TOGGLE_MEDIA_IN_SCENE',
}

export type TScenePushedSignalEvent = SignalEvent<string>;

export type TSceneInProgramChangedSignalEvent = SignalEvent<IGraphicsScene>;

export type TQnaQuestionSelectedSignalEvent = SignalEvent<IQnAQuestion>;

export type TPollObjectUpdatedSignalEvent = SignalEvent<IPoll>;

export type TGuestUpdatedSignalEvent = SignalEvent<IGuestSeat>;

export type TGuestDeletedSignalEvent = SignalEvent<string>;

export type TPollObjectRemovedSignalEvent = SignalEvent<string>;

export type TGraphicDeletedSignalEvent = SignalEvent<{
  graphicId: string;
  graphicType: EGraphicsDescriptorType;
}>;

export enum EGuestSignal {
  SESSION_JOINED = 'SESSION_JOINED',
  SESSION_LEFT = 'SESSION_LEFT',
}

export enum EGuestMuteSignal {
  GUEST_MUTE_REQUESTED = 'GUEST_MUTE_REQUESTED',
  GUEST_MUTE_ACKNOWLEDGED = 'GUEST_MUTE_ACKNOWLEDGED',
  GUEST_UNMUTE_REQUESTED = 'GUEST_UNMUTE_REQUESTED',
  GUEST_UNMUTE_ACKNOWLEDGED = 'GUEST_UNMUTE_ACKNOWLEDGED',
}
